import {
  useCanAccess,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";

import type { Shipment } from "./types";

const signingUrl = (token: string) =>
  `${window.location.origin}/#/sign/${token}`;

// Staff view of the e-signature: get the customer link, see who signed, or (admin) clear it.
export const SignatureSection = () => {
  const r = useRecordContext<Shipment>();
  const translate = useTranslate();
  const notify = useNotify();
  const [update, { isPending }] = useUpdate();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  if (!r || r.status === "void") return null;

  const copyLink = async () => {
    let token = r.sign_token;
    if (!token) {
      token = crypto.randomUUID();
      await update(
        "shipments",
        { id: r.id, data: { sign_token: token }, previousData: r },
        { mutationMode: "pessimistic", returnPromise: true },
      );
    }
    const url = signingUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      notify("egi.sign.link_copied");
    } catch {
      window.prompt(translate("egi.sign.link_prompt"), url);
    }
  };

  const clear = () => {
    if (!window.confirm(translate("egi.sign.clear_confirm"))) return;
    update(
      "shipments",
      { id: r.id, data: { signed_at: null }, previousData: r },
      {
        mutationMode: "pessimistic",
        onSuccess: () => notify("egi.sign.cleared"),
        onError: (e: unknown) =>
          notify(e instanceof Error ? e.message : "Update failed", {
            type: "error",
          }),
      },
    );
  };

  if (r.signed_at) {
    return (
      <div className="flex flex-wrap items-center gap-4 rounded-md border border-green-600/40 bg-green-50 p-3 text-sm dark:bg-green-950/30">
        {r.signature_png ? (
          <img
            src={r.signature_png}
            alt={r.signer_name ?? ""}
            className="h-12 rounded bg-white"
          />
        ) : null}
        <div className="flex-1 min-w-48">
          <div className="font-semibold">
            {translate("egi.sign.signed_by", {
              name: r.signer_name,
              date: new Date(r.signed_at).toLocaleString(),
            })}
          </div>
          <div className="text-muted-foreground">
            IP {r.signer_ip ?? "-"}, {translate("egi.sign.fingerprint")}{" "}
            {r.signed_hash?.slice(0, 12)}
          </div>
        </div>
        {isAdmin ? (
          <Button
            variant="outline"
            size="sm"
            onClick={clear}
            disabled={isPending}
          >
            {translate("egi.sign.clear_signature")}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-dashed p-3 text-sm">
      <span className="flex-1 min-w-48 text-muted-foreground">
        {translate("egi.sign.not_signed")}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={copyLink}
        disabled={isPending}
      >
        <Link2 className="size-4" />
        {translate("egi.sign.copy_link")}
      </Button>
    </div>
  );
};
